import { Badge } from "@/components/ui/badge";
import { MessageSquare, Users } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export function AdminFeedbackAndUsers({ metrics, users, setUsers }) {
  return (
    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
      <div
        data-testid="admin-feedback-summary"
        className="cre-surface rounded-sm p-5"
      >
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-blue-300" strokeWidth={1.5} />
          <div className="cre-overline text-blue-300">Feedback</div>
        </div>
        <div className="mt-3 font-heading text-3xl font-black tracking-tight text-slate-900">
          {metrics.feedback.total}
        </div>
        <div className="mt-3 space-y-1.5">
          {Object.entries(metrics.feedback.by_rating).map(([k, v]) => (
            <div
              key={k}
              data-testid={`admin-feedback-${k}`}
              className="flex items-center justify-between text-sm"
            >
              <span className="text-zinc-300 capitalize">
                {k.replaceAll("_", " ")}
              </span>
              <span className="font-mono text-zinc-200">{v}</span>
            </div>
          ))}
          {metrics.feedback.total === 0 ? (
            <div className="text-xs text-zinc-500">No feedback yet.</div>
          ) : null}
        </div>
      </div>

      <div data-testid="admin-users-list" className="cre-surface rounded-sm p-5">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-emerald-300" strokeWidth={1.5} />
          <div className="cre-overline text-emerald-300">Users</div>
        </div>
        <div className="mt-3 space-y-1.5 max-h-64 overflow-auto pr-1">
          {users.map((u) => (
            <div
              key={u.id}
              data-testid={`admin-user-row-${u.id}`}
              className="flex items-center justify-between gap-2 text-sm border-b border-white/5 pb-1.5"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-zinc-100">{u.email}</div>
                <div className="text-[11px] font-mono text-zinc-500 flex items-center gap-2 flex-wrap">
                  <span>{u.role}</span>
                  <span>·</span>
                  <span
                    data-testid={`admin-user-plan-${u.id}`}
                    className={
                      u.plan === "pro" ? "text-violet-700" : "text-slate-500"
                    }
                  >
                    {(u.plan || "free").toUpperCase()}
                  </span>
                  <span>·</span>
                  <span>
                    {u.used_analysis_count ?? 0}/{u.monthly_analysis_limit ?? 3}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Badge
                  variant="outline"
                  className="rounded-sm font-mono text-[10px] border-white/15"
                >
                  {u.analyses} analyses
                </Badge>
                {u.role !== "admin" ? (
                  <button
                    type="button"
                    data-testid={`admin-user-toggle-plan-${u.id}`}
                    onClick={async () => {
                      const next = u.plan === "pro" ? "free" : "pro";
                      try {
                        await api.adminSetUserPlan(u.id, {
                          plan: next,
                          reset_usage: true,
                        });
                        toast.success(`Set ${u.email} to ${next.toUpperCase()}`);
                        const fresh = await api.adminUsers();
                        setUsers(fresh);
                      } catch (e) {
                        toast.error("Could not update plan");
                      }
                    }}
                    className="text-[10px] font-mono uppercase tracking-wider border border-slate-200 hover:border-slate-300 px-2 py-0.5 rounded-sm text-slate-600"
                  >
                    {u.plan === "pro" ? "→ Free" : "→ Pro"}
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
